import { supabase } from '../lib/supabase';
import { PASSWORD_RECOVERY_REDIRECT } from '../config/authUrls';

export interface PartnerApplicationData {
  // Business Information
  business_name: string;
  business_type: string;
  business_address: string;
  business_description: string;
  establishment_size: string;
  
  // Contact Information
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  
  // Business Details
  target_demographics: string;
  current_marketing_channels: string[];
  website_url?: string;
  social_media_urls?: Record<string, string>;
  
  // Partnership Interest
  desired_offer_types: string[];
  expected_customer_volume: string;
  marketing_goals: string;
  
  // Additional Information
  business_license_number?: string;
  tax_id?: string;
  vat_number?: string;
  applicant_notes?: string;
}

export const partnerApplicationService = {
  /**
   * Submit a partner application (public access)
   */
  submitApplication: async (applicationData: PartnerApplicationData) => {
    try {
      // CRITICAL FIX: Remove ALL array validation - pass data directly to database
      // Supabase will handle array conversion, not our code
      const { data, error } = await supabase
        .from('partners')
        .insert({
          business_name: applicationData.business_name,
          business_type: applicationData.business_type,
          business_address: applicationData.business_address,
          business_description: applicationData.business_description,
          establishment_size: applicationData.establishment_size,
          contact_name: applicationData.contact_name,
          contact_email: applicationData.contact_email,
          contact_phone: applicationData.contact_phone,
          target_demographics: applicationData.target_demographics,
          current_marketing_channels: applicationData.current_marketing_channels,
          website_url: applicationData.website_url,
          social_media_urls: applicationData.social_media_urls,
          desired_offer_types: applicationData.desired_offer_types,
          expected_customer_volume: applicationData.expected_customer_volume,
          marketing_goals: applicationData.marketing_goals,
          business_license_number: applicationData.business_license_number,
          tax_id: applicationData.tax_id,
          vat_number: applicationData.vat_number,
          applicant_notes: applicationData.applicant_notes,
          status: 'pending',
          application_date: new Date().toISOString(),
        })
        .select()
        .single();

      if (error) throw error;
      return { success: true, data };
    } catch (error: any) {
      console.error('Error submitting partner application:', error);
      return { success: false, error: error.message };
    }
  },

  /**
   * Check if email already has a pending application
   */
  checkExistingApplication: async (email: string) => {
    try {
      // CRITICAL: Add guard check to prevent null.from() errors
      if (!supabase || typeof (supabase as any).from !== "function") {
        throw new Error("Supabase client not initialized: expected supabase.from to be a function. Check imports/exports.");
      }

      // ULTIMATE FIX: Replace .in() method completely to avoid minification issues
      // Use .or() with explicit equality checks instead of .in() which uses Array.from internally
      const { data, error } = await supabase
        .from('partners')
        .select('id, status, application_date')
        .eq('contact_email', email)
        .or('status.eq.pending,status.eq.active')  // Direct .or() instead of .in()
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      
      // Direct return with explicit null check
      if (data) {
        return { exists: true, data: data };
      }
      
      return { exists: false, data: null };
    } catch (error: any) {
      console.error('Error checking existing application:', error);
      return { exists: false, data: null };
    }
  },

  /**
   * Check if a partner account already exists with the given email
   * NOTE: This now uses database-level checks instead of auth admin methods
   */
  checkExistingPartnerAccount: async (email: string): Promise<{ 
    exists: boolean; 
    hasAuth: boolean; 
    hasProfile: boolean;
    partnerId?: string;
    message?: string;
  }> => {
    try {
      // Check user_profiles table (auth check is not possible from browser)
      const { data: userProfile, error } = await supabase
        .from('user_profiles')
        .select('id, partner_id, role')
        .eq('email', email)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error checking user profile:', error);
      }

      const hasProfile = !!userProfile;
      // We can't check auth.users from the browser, so we assume if profile exists, auth likely exists too
      const hasAuth = hasProfile; 
      const exists = hasProfile;

      let message = '';
      if (exists) {
        message = 'Account already exists in the system. Partner can log in with existing credentials.';
      }

      return {
        exists,
        hasAuth,
        hasProfile,
        partnerId: userProfile?.partner_id,
        message
      };
    } catch (error: any) {
      console.error('Error checking existing partner account:', error);
      return {
        exists: false,
        hasAuth: false,
        hasProfile: false,
        message: undefined
      };
    }
  },

  /**
   * Pre-approve a partner application and send calendar scheduling link
   */
  preApprovePartner: async (partnerId: string): Promise<{ success: boolean; message: string; calendarLink?: string; application?: any }> => {
    try {
      // Direct update approach to bypass PostgREST cache issues
      const { data: updatedPartner, error: updateError } = await supabase
        .from('partners')
        .update({ 
          status: 'pre_approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId)
        .eq('status', 'pending')  // Only update if currently pending
        .select()
        .single();

      if (updateError) {
        throw new Error(updateError.message);
      }

      if (!updatedPartner) {
        throw new Error('Partner not found or not in pending status');
      }

      return {
        success: true,
        message: 'Partner pre-approved successfully',
        application: updatedPartner
      };
    } catch (error: any) {
      console.error('Error pre-approving partner:', error);
      throw new Error(error.message || 'Failed to pre-approve partner application');
    }
  },

  /**
   * Record calendar slot selection by partner
   */
  recordCalendarSlot: async (partnerId: string, timeSlot: string): Promise<{ success: boolean; message: string }> => {
    try {
      console.log('Recording calendar slot:', { partnerId, timeSlot });
      
      // Direct RPC call with minimal validation to avoid minification issues
      const { data, error } = await supabase
        .rpc('record_calendar_slot_selection', {
          partner_id_param: partnerId,
          time_slot_param: timeSlot
        });

      console.log('RPC call complete - data:', data, 'error:', error);

      // Handle error case
      if (error) {
        console.error('Database error:', error);
        return {
          success: false,
          message: error.message || 'Database error occurred'
        };
      }

      // Handle no data response
      if (!data) {
        console.error('No data returned from database');
        return {
          success: false,
          message: 'No response from database'
        };
      }

      // Extract result - handle both array and object responses
      const result = Array.isArray(data) ? data[0] : data;
      
      console.log('Calendar slot recording result:', result);

      return {
        success: result?.success || false,
        message: result?.message || 'Operation completed'
      };
    } catch (error: any) {
      console.error('Unexpected error in recordCalendarSlot:', error);
      
      return {
        success: false,
        message: error?.message || 'Failed to record calendar slot selection'
      };
    }
  },

  /**
   * Mark onboarding call as completed
   * NOTE: This now automatically sends the contract and progresses to contract signing stage
   */
  completeOnboardingCall: async (partnerId: string, notes?: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.rpc('complete_onboarding_call', {
        partner_id_param: partnerId,
        call_notes_param: notes || null
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('No response from database');
      }

      return {
        success: data[0].success,
        message: data[0].message
      };
    } catch (error: any) {
      console.error('Error completing onboarding call:', error);
      throw new Error(error.message || 'Failed to complete onboarding call');
    }
  },

  /**
   * Skip onboarding call and proceed to next stage
   * Marks the onboarding call as completed without requiring an actual call
   */
  skipOnboardingCall: async (partnerId: string): Promise<{ success: boolean; message: string; application?: any }> => {
    try {
      // Direct update to mark onboarding call as completed
      const { data: updatedPartner, error: updateError } = await supabase
        .from('partners')
        .update({ 
          onboarding_call_completed: true,
          onboarding_call_notes: 'Onboarding call skipped - proceeding directly to contract signing',
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId)
        .eq('status', 'pre_approved')  // Only update if currently pre-approved
        .select()
        .single();

      if (updateError) {
        throw new Error(updateError.message);
      }

      if (!updatedPartner) {
        throw new Error('Partner not found or not in pre-approved status');
      }

      return {
        success: true,
        message: 'Onboarding call skipped successfully - partner can now proceed to contract signing',
        application: updatedPartner
      };
    } catch (error: any) {
      console.error('Error skipping onboarding call:', error);
      throw new Error(error.message || 'Failed to skip onboarding call');
    }
  },

  /**
   * Send partnership contract to partner
   */
  sendPartnerContract: async (partnerId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.rpc('send_partner_contract', {
        partner_id_param: partnerId
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('No response from database');
      }

      return {
        success: data[0].success,
        message: data[0].message
      };
    } catch (error: any) {
      console.error('Error sending contract:', error);
      throw new Error(error.message || 'Failed to send partnership contract');
    }
  },

  /**
   * Record contract signature
   */
  recordContractSignature: async (partnerId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.rpc('record_contract_signature', {
        partner_uuid: partnerId
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('No response from database');
      }

      return {
        success: data[0].success,
        message: data[0].message
      };
    } catch (error: any) {
      console.error('Error recording contract signature:', error);
      throw new Error(error.message || 'Failed to record contract signature');
    }
  },

  /**
   * Complete partner onboarding and activate account
   */
  completePartnerOnboarding: async (partnerId: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data, error } = await supabase.rpc('complete_partner_onboarding', {
        partner_id_param: partnerId
      });

      if (error) throw error;

      if (!data || data.length === 0) {
        throw new Error('No response from database');
      }

      return {
        success: data[0].success,
        message: data[0].message
      };
    } catch (error: any) {
      console.error('Error completing onboarding:', error);
      throw new Error(error.message || 'Failed to complete partner onboarding');
    }
  },

  /**
   * Add contract conditions (Substage 1)
   */
  async addContractConditions(
    partnerId: string, 
    notes?: string,
    monthlyPrice?: number,
    contractTimeframe?: 3 | 6 | 9 | 12,
    paymentTerms?: 'upfront' | 'monthly'
  ) {
    const { data, error } = await supabase.rpc('add_contract_conditions', {
      p_partner_id: partnerId,
      p_notes: notes || null,
      p_monthly_price: monthlyPrice || null,
      p_contract_timeframe: contractTimeframe || null,
      p_payment_terms: paymentTerms || null
    });

    if (error) {
      console.error('Error adding contract conditions:', error);
      throw error;
    }

    return data?.[0] || { success: false, message: 'Unknown error' };
  },

  /**
   * Send contract to partner (Substage 2)
   */
  async sendContractToPartner(partnerId: string, adminId: string, contractPdfUrl?: string) {
    const { data, error } = await supabase.rpc('send_partner_contract', {
      partner_uuid: partnerId,
      admin_user_uuid: adminId
    });

    if (error) {
      console.error('Error sending contract:', error);
      throw error;
    }

    return data?.[0] || { success: false, message: 'Unknown error' };
  },

  /**
   * Upload contract PDF to Supabase Storage
   */
  async uploadContractPdf(partnerId: string, partnerCode: string, file: File): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      // Validate file type
      if (file.type !== 'application/pdf') {
        return { success: false, error: 'Only PDF files are allowed' };
      }

      // Validate file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        return { success: false, error: 'File size must be less than 10MB' };
      }

      // Create folder structure: partner_code/contract_timestamp.pdf
      const timestamp = new Date().getTime();
      const fileName = `${partnerCode}/contract_${timestamp}.pdf`;

      // Upload to Supabase Storage
      const { data, error } = await supabase.storage
        .from('contract-documents')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('Storage upload error:', error);
        return { success: false, error: error.message };
      }

      // Get the file path (not public URL since bucket is private)
      const filePath = data.path;

      return { 
        success: true, 
        url: filePath // Store path, will generate signed URL when needed
      };
    } catch (error: any) {
      console.error('Upload contract PDF error:', error);
      return { success: false, error: error.message || 'Upload failed' };
    }
  },

  /**
   * Get signed URL for contract PDF (for viewing)
   */
  async getContractPdfSignedUrl(filePath: string): Promise<{ success: boolean; url?: string; error?: string }> {
    try {
      const { data, error } = await supabase.storage
        .from('contract-documents')
        .createSignedUrl(filePath, 3600); // Valid for 1 hour

      if (error) {
        console.error('Error creating signed URL:', error);
        return { success: false, error: error.message };
      }

      return { success: true, url: data.signedUrl };
    } catch (error: any) {
      console.error('Get signed URL error:', error);
      return { success: false, error: error.message || 'Failed to get signed URL' };
    }
  },

  /**
   * Get all pending and in-progress partner applications for admin workflow
   */
  getPendingApplications: async () => {
    try {
      const { data, error } = await supabase
        .from('partners')
        .select('*')
        .or('status.eq.pending,status.eq.pre_approved')
        .order('application_date', { ascending: true });
      
      if (error) throw error;

      // ULTIMATE FIX: Return data directly or empty array literal
      // No Array.isArray, no type checking, no processing
      // Let TypeScript handle type safety, minifier can't break literal array
      return data || [];
    } catch (error: any) {
      console.error('Error fetching pending applications:', error);
      // Return literal empty array on error
      return [];
    }
  },

  /**
   * Approve a partner application
   */
  approvePartner: async (partnerId: string, approvedBy: string, commissionRate?: number): Promise<{ success: boolean; message: string }> => {
    try {
      const { data: updatedPartner, error: updateError } = await supabase
        .from('partners')
        .update({ 
          status: 'active',
          approved_by: approvedBy,
          approved_date: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId)
        .select()
        .single();

      if (updateError) {
        throw new Error(updateError.message);
      }

      return {
        success: true,
        message: 'Partner approved successfully'
      };
    } catch (error: any) {
      console.error('Error approving partner:', error);
      throw new Error(error.message || 'Failed to approve partner');
    }
  },

  /**
   * Reject a partner application
   */
  rejectPartner: async (partnerId: string, reason: string): Promise<{ success: boolean; message: string }> => {
    try {
      const { data: updatedPartner, error: updateError } = await supabase
        .from('partners')
        .update({ 
          status: 'rejected',
          rejection_reason: reason,
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId)
        .select()
        .single();

      if (updateError) {
        throw new Error(updateError.message);
      }

      return {
        success: true,
        message: 'Partner rejected successfully'
      };
    } catch (error: any) {
      console.error('Error rejecting partner:', error);
      throw new Error(error.message || 'Failed to reject partner');
    }
  },

  /**
   * Send magic link to partner email for account activation
   * FIXED: Properly sends magic link instead of confirmation email
   */
  sendPartnerMagicLink: async (
    partnerId: string,
    email: string,
    fullName: string,
    phone: string
  ): Promise<{ success: boolean; message: string; partnerId?: string }> => {
    try {
      console.log('🚀 MAGIC LINK: Starting magic link send process for partner:', { partnerId, email, fullName });
      
      // 🔥 CRITICAL FIX: Explicitly construct the redirect URL to ensure it points to magic-link-confirmation
      // This prevents any Supabase template configuration from overriding it
      const redirectUrl = `${window.location.origin}/magic-link-confirmation`;
      console.log('🔗 MAGIC LINK: Redirect URL set to:', redirectUrl);
      
      // 🔥 CRITICAL FIX: Use signInWithOtp for ACTUAL magic links (not signUp which creates confirmation emails)
      const { error: magicLinkError } = await supabase.auth.signInWithOtp({
        email: email,
        options: {
          // CRITICAL: Explicitly set emailRedirectTo to magic-link-confirmation page
          emailRedirectTo: redirectUrl,
          shouldCreateUser: true, // Allow new user creation via magic link
          data: {
            full_name: fullName,
            phone: phone,
            partner_id: partnerId,
            role: 'partner'
          }
        }
      });

      if (magicLinkError) {
        console.error('❌ MAGIC LINK ERROR:', magicLinkError);
        throw magicLinkError;
      }

      console.log('✅ MAGIC LINK: Magic link sent successfully to:', email);
      console.log('✅ MAGIC LINK: Email will redirect to:', redirectUrl);
      
      // Update partner status to active and mark credentials as sent
      const { error: statusError } = await supabase
        .from('partners')
        .update({ 
          status: 'active',
          credentials_sent_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId)
        .eq('status', 'pre_approved');

      if (statusError) {
        console.error('⚠️ MAGIC LINK: Error updating partner status:', statusError);
        // Don't throw - magic link was sent successfully
      } else {
        console.log('✅ MAGIC LINK: Partner status updated to active');
      }

      return {
        success: true,
        message: `✅ Magic link sent! Partner will receive an email with a secure login link that redirects to: ${window.location.origin}/magic-link-confirmation. The link expires in 1 hour.`,
        partnerId: partnerId
      };
    } catch (error: any) {
      console.error('❌ MAGIC LINK: Unexpected error during magic link send:', {
        error,
        message: error.message,
        stack: error.stack
      });
      
      // Handle specific Supabase auth errors
      if (error.message?.includes('rate limit')) {
        return {
          success: false,
          message: 'Too many requests. Please wait a moment and try again.'
        };
      }
      
      if (error.message?.includes('invalid email')) {
        return {
          success: false,
          message: 'Invalid email address. Please check and try again.'
        };
      }
      
      throw new Error(error.message || 'Failed to send magic link');
    }
  },

  /**
   * Create partner account with authentication credentials using admin function
   * Then trigger Supabase's built-in password reset email immediately
   */
  createPartnerAccountWithAuth: async (
    partnerId: string,
    email: string,
    fullName: string,
    phone: string,
    tempPassword: string = 'algernon99'
  ): Promise<{ success: boolean; message: string; partnerId?: string; authUserId?: string }> => {
    try {
      console.log('Creating partner account with auth:', { partnerId, email, fullName });
      
      // Pre-check: See if account already exists
      const existingCheck = await partnerApplicationService.checkExistingPartnerAccount(email);
      
      if (existingCheck.exists && existingCheck.hasProfile) {
        console.log('Account already exists - activating partner');
        
        const { error: updateError } = await supabase
          .from('partners')
          .update({ 
            status: 'active',
            credentials_sent_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', partnerId)
          .eq('status', 'pre_approved');

        if (updateError) {
          console.error('Error updating partner status:', updateError);
        }

        return {
          success: true,
          message: '✅ Account already exists! Partner has been activated and can log in with existing credentials.',
          partnerId: existingCheck.partnerId,
          authUserId: undefined
        };
      }

      // Get partner details first
      const { data: partner, error: fetchError } = await supabase
        .from('partners')
        .select('*')
        .eq('id', partnerId)
        .single();

      if (fetchError) throw fetchError;
      if (!partner) throw new Error('Partner not found');

      const safeVatNumber = partner.vat_number || '';
      const safeBillingAddress = partner.billing_address || partner.business_address || '';

      console.log('Calling admin_create_partner_with_auth function...');
      
      const { data, error } = await supabase.rpc('admin_create_partner_with_auth', {
        p_business_name: partner.business_name,
        p_billing_address: safeBillingAddress,
        p_vat_number: safeVatNumber,
        p_contact_email: email,
        p_contact_phone: phone,
        p_contact_name: fullName,
        p_temp_password: 'algernon99'
      });

      console.log('RPC response:', { data, error });

      if (error) {
        console.error('RPC error:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        throw new Error('No response from database');
      }

      let result = data[0];
      console.log('Database function result:', result);

      if (!result.success) {
        const duplicateIndicators = [
          'already registered',
          'race condition',
          'conflicts with an existing record',
          'already exists',
          'user profile already exists',
          'duplicate'
        ];
        
        const isDuplicateError = duplicateIndicators.some(indicator => 
          result.message?.toLowerCase().includes(indicator.toLowerCase())
        );
        
        if (isDuplicateError) {
          console.log('Detected duplicate email - treating as existing account');
          
          const { error: updateError } = await supabase
            .from('partners')
            .update({ 
              status: 'active',
              credentials_sent_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .eq('id', partnerId)
            .eq('status', 'pre_approved');

          if (updateError) {
            console.error('Error updating partner status:', updateError);
          }

          return {
            success: true,
            message: '✅ Account already exists! Partner has been activated and can log in with existing credentials.',
            partnerId: result.partner_id || partnerId,
            authUserId: result.auth_user_id
          };
        }
        
        throw new Error(result.message);
      }

      console.log('✅ Account created - now triggering Supabase password reset email');
      
      // 🎯 CRITICAL: Use shared constant for consistent password recovery redirect
      // All password recovery emails MUST redirect to /partner-password-reset
      console.log('🔐 PASSWORD RESET: Sending recovery email for partner account creation');
      console.log('📧 Email:', email);
      console.log('🔗 REDIRECT TARGET:', PASSWORD_RECOVERY_REDIRECT);
      console.log('⚠️  IMPORTANT: Check DevTools Network tab - POST /auth/v1/recover body must include redirect_to=' + PASSWORD_RECOVERY_REDIRECT);
      
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: PASSWORD_RECOVERY_REDIRECT
      });

      if (resetError) {
        console.error('❌ CRITICAL: Password reset email failed:', resetError);
        throw new Error(`Account created but password reset email failed to send: ${resetError.message}. Please contact support or try resending the password reset email.`);
      }

      console.log('✅ Supabase password reset email sent successfully');
      console.log('📧 Recovery link will redirect to:', PASSWORD_RECOVERY_REDIRECT);
      
      const { error: statusError } = await supabase
        .from('partners')
        .update({ 
          status: 'active',
          credentials_sent_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId)
        .eq('status', 'pre_approved');

      if (statusError) {
        console.error('Error updating partner status to active:', statusError);
      }

      return {
        success: true,
        message: '✅ Partner account created! Password reset email sent - partner will create their own password.',
        partnerId: result.partner_id,
        authUserId: result.auth_user_id
      };
    } catch (error: any) {
      console.error('Error creating partner account with auth:', error);
      
      let errorMessage = error.message || 'Failed to create partner account';
      
      const duplicateIndicators = [
        'already registered',
        'race condition',
        'conflicts with an existing record',
        'already exists',
        'user profile already exists',
        'duplicate'
      ];
      
      const isDuplicateError = duplicateIndicators.some(indicator => 
        errorMessage.toLowerCase().includes(indicator.toLowerCase())
      );
      
      if (isDuplicateError) {
        console.log('Caught duplicate error in exception handler - treating as existing account');
        
        const { error: updateError } = await supabase
          .from('partners')
          .update({ 
            status: 'active',
            credentials_sent_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', partnerId)
          .eq('status', 'pre_approved');

        if (updateError) {
          console.error('Error updating partner status in catch:', updateError);
        }

        return {
          success: true,
          message: '✅ Account already exists! Partner has been activated and can log in with existing credentials.',
          partnerId: partnerId,
          authUserId: undefined
        };
      }
      
      throw new Error(errorMessage);
    }
  },

  /**
   * Create partner account with authentication credentials
   * @deprecated Use createPartnerAccountWithAuth instead
   */
  createPartnerAccount: async (partnerId: string, email: string, tempPassword: string): Promise<{ success: boolean; message: string }> => {
    try {
      // This would typically involve creating auth credentials
      // For now, we'll just update the credentials_sent_at timestamp
      const { error } = await supabase
        .from('partners')
        .update({ 
          credentials_sent_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', partnerId);

      if (error) throw error;

      return {
        success: true,
        message: 'Partner account created successfully'
      };
    } catch (error: any) {
      console.error('Error creating partner account:', error);
      throw new Error(error.message || 'Failed to create partner account');
    }
  },

  /**
   * Delete a partner application permanently
   */
  deletePartnerApplication: async (partnerId: string): Promise<{ success: boolean; message: string }> => {
    try {
      console.log('Starting partner deletion process for partner ID:', partnerId);
      
      // Step 1: Delete all related offers first (CASCADE manually)
      console.log('Step 1: Deleting related offers...');
      const { error: offersDeleteError, count: offersCount } = await supabase
        .from('offers')
        .delete()
        .eq('partner_id', partnerId);

      if (offersDeleteError) {
        console.error('Error deleting offers:', offersDeleteError);
        throw new Error(`Failed to delete partner offers: ${offersDeleteError.message}`);
      }
      
      console.log(`Deleted ${offersCount || 0} offers associated with partner`);

      // Step 2: Delete the user_profile if exists (to clean up auth linkage)
      console.log('Step 2: Deleting user profile if exists...');
      const { error: profileDeleteError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('partner_id', partnerId);

      if (profileDeleteError) {
        // Log but don't fail - profile might not exist
        console.warn('Warning deleting user profile:', profileDeleteError.message);
      }

      // Step 3: Finally delete the partner record
      console.log('Step 3: Deleting partner record...');
      const { error: partnerDeleteError } = await supabase
        .from('partners')
        .delete()
        .eq('id', partnerId);

      if (partnerDeleteError) {
        console.error('Error deleting partner:', partnerDeleteError);
        throw new Error(`Failed to delete partner: ${partnerDeleteError.message}`);
      }

      console.log('Partner deletion completed successfully');
      
      return {
        success: true,
        message: 'Partner application deleted successfully'
      };
    } catch (error: any) {
      console.error('Error in deletePartnerApplication:', error);
      return {
        success: false,
        message: error.message || 'Failed to delete partner application'
      };
    }
  },

  /**
   * Delete a partner application (alias method)
   * This method uses the correct table name 'partners' instead of non-existent 'partner_applications'
   */
  async deleteApplication(applicationId: string): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('Starting application deletion for ID:', applicationId);

      // Step 1: Delete all related offers first
      const { error: offersDeleteError } = await supabase
        .from('offers')
        .delete()
        .eq('partner_id', applicationId);

      if (offersDeleteError) {
        console.error('Error deleting offers:', offersDeleteError);
        return { 
          success: false, 
          error: `Failed to delete partner offers: ${offersDeleteError.message}` 
        };
      }

      // Step 2: Delete the user_profile if exists
      const { error: profileDeleteError } = await supabase
        .from('user_profiles')
        .delete()
        .eq('partner_id', applicationId);

      if (profileDeleteError) {
        console.warn('Warning deleting user profile:', profileDeleteError.message);
      }

      // Step 3: Delete the partner record from 'partners' table (not 'partner_applications')
      const { error: deleteError } = await supabase
        .from('partners')
        .delete()
        .eq('id', applicationId);

      if (deleteError) {
        console.error('Error deleting partner:', deleteError);
        return { 
          success: false, 
          error: `Failed to delete partner: ${deleteError.message}` 
        };
      }

      console.log('Application deleted successfully');
      return { success: true };
    } catch (error) {
      console.error('Unexpected error during application deletion:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred' 
      };
    }
  }
};

export default partnerApplicationService;